import {
    Box, Typography, Button, Modal, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Skeleton, Select, MenuItem,
    FormControl, InputLabel, ToggleButton, ToggleButtonGroup, IconButton,
    Tooltip, Alert
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Toast } from 'primereact/toast';
import { Menu as PrimeMenu } from 'primereact/menu';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizSharpIcon from '@mui/icons-material/MoreHorizSharp';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CustomAxios from "../../utils/CustomAxios";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const RATE_BASES = [
    { value: 'fixed_divisor', label: 'Fixed divisor' },
    { value: 'calendar_days', label: 'Calendar days' },
    { value: 'working_days', label: 'Actual working days' },
];

const OVERFLOW_OPTIONS = [
    { value: 'auto_lop', label: 'Auto-convert to loss of pay' },
    { value: 'block', label: 'Block the request' },
];

const MISSING_PUNCH_OPTIONS = [
    { value: 'absent_unless_regularized', label: 'Counts as absent unless regularized' },
    { value: 'present', label: 'Counts as present' },
    { value: 'half_day', label: 'Counts as half day' },
];

const EMPTY_RULES = {
    per_day_rate_basis: 'fixed_divisor',
    rate_divisor: 26,
    lates_per_deduction: 3,
    late_deduction_days: '0.5',
    permission_free_hours: '2.0',
    missing_punch_handling: 'absent_unless_regularized',
    regularization_window_days: 3,
    leave_balance_overflow: 'auto_lop',
    comp_off_validity_days: 90,
    leave_is_paid: true,
    comp_off_is_paid: true,
    on_duty_is_paid: true,
    permission_is_paid: true,
    is_active: true,
};

function Chip({ bg, border, color, label }) {
    return (
        <Box sx={{
            display: 'inline-flex', justifyContent: 'center',
            backgroundColor: bg, border: '1px solid', borderColor: border,
            borderRadius: '2px', padding: '1px 8px', whiteSpace: 'nowrap'
        }}>
            <Typography sx={{ fontSize: '11px', lineHeight: '20px', color, textTransform: 'uppercase' }}>
                {label}
            </Typography>
        </Box>
    );
}

export function StaffPayrollConfig() {
    const selectedHub = useSelector(state => state.user.selected_hub);
    const inventory = useSelector(state => state.inventory.inventory);
    const toast = useRef(null);
    const isSuperAdmin = localStorage.getItem('role') === 'super_admin';

    const salaryMenuRefs = useRef([]);
    const lockMenuRefs = useRef([]);

    const [rulesList, setRulesList] = useState([]);
    const [effective, setEffective] = useState(null);
    const [form, setForm] = useState(EMPTY_RULES);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [salaries, setSalaries] = useState([]);
    const [staffOptions, setStaffOptions] = useState([]);
    const [salaryModal, setSalaryModal] = useState(false);
    const [salaryForm, setSalaryForm] = useState({
        staff_id: '', monthly_gross: '', effective_from: dayjs().format('YYYY-MM-DD'),
    });

    const [locks, setLocks] = useState([]);

    const hubId = useMemo(() => {
        const normalize = (value) => {
            if (value === null || value === undefined) return null;
            if (Array.isArray(value)) return normalize(value[0]);
            const n = Number(value);
            return Number.isFinite(n) ? n : null;
        };
        const fromStore = normalize(selectedHub);
        if (fromStore) return fromStore;
        const stored = localStorage.getItem('selected_hub');
        if (!stored) return null;
        try {
            return normalize(JSON.parse(stored));
        } catch (e) {
            return normalize(stored);
        }
    }, [selectedHub]);

    const showToast = (severity, summary, detail) => {
        if (toast.current) toast.current.show({ severity, summary, detail, life: 4500 });
    };

    const errorMessage = (error, fallback) => {
        const detail = error?.response?.data?.error;
        if (!detail) return fallback;
        if (typeof detail === 'string') return detail;
        const firstKey = Object.keys(detail)[0];
        const firstValue = detail[firstKey];
        return Array.isArray(firstValue) ? firstValue[0] : String(firstValue);
    };

    useEffect(() => {
        if (!hubId) return;
        fetchAll();
    }, [hubId]);

    const fetchAll = async () => {
        setLoading(true);
        await Promise.all([fetchRules(), fetchSalaries(), fetchLocks(), fetchStaff()]);
        setLoading(false);
    };

    const fetchRules = async () => {
        try {
            const res = await CustomAxios.get('staff/payroll-rules/', { params: { hub_id: hubId } });
            setRulesList(res.data.results || []);
            setEffective(res.data.effective || null);

            // Prefill the form from whatever currently applies, so saving an
            // override starts from the inherited values rather than blank.
            const own = (res.data.results || []).find(r => r.hub === hubId);
            const source = own || res.data.effective;
            if (source) {
                setForm({ ...EMPTY_RULES, ...source });
                setEditingId(own ? own.id : null);
            } else {
                setForm(EMPTY_RULES);
                setEditingId(null);
            }
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Failed to load payroll rules'));
        }
    };

    const fetchSalaries = async () => {
        try {
            const res = await CustomAxios.get('staff/salary-config/', { params: { hub_id: hubId } });
            setSalaries(res.data || []);
            salaryMenuRefs.current = (res.data || []).map((_, i) => salaryMenuRefs.current[i] || React.createRef());
        } catch (error) {
            setSalaries([]);
        }
    };

    const fetchLocks = async () => {
        try {
            const res = await CustomAxios.get('staff/payroll/locks/', { params: { hub_id: hubId } });
            setLocks(res.data || []);
            lockMenuRefs.current = (res.data || []).map((_, i) => lockMenuRefs.current[i] || React.createRef());
        } catch (error) {
            setLocks([]);
        }
    };

    const fetchStaff = async () => {
        try {
            const res = await CustomAxios.get('staff/options/', { params: { hub_id: hubId } });
            setStaffOptions(res.data || []);
        } catch (error) {
            setStaffOptions([]);
        }
    };

    const ownOverride = rulesList.find(r => r.hub === hubId);
    const globalRow = rulesList.find(r => r.hub === null);

    const saveRules = async () => {
        if (Number(form.rate_divisor) <= 0) {
            showToast('error', 'Validation Error', 'The divisor must be greater than zero');
            return;
        }
        try {
            setSaving(true);
            const payload = { ...form, hub_id: hubId };
            if (editingId) {
                await CustomAxios.put(`staff/payroll-rules/${editingId}/`, payload);
            } else {
                await CustomAxios.post('staff/payroll-rules/', payload);
            }
            showToast('success', 'Saved', editingId
                ? 'Payroll rules updated'
                : 'Hub override created — other hubs are unaffected');
            fetchRules();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Could not save the rules'));
        } finally {
            setSaving(false);
        }
    };

    const removeOverride = async () => {
        if (!ownOverride) return;
        if (!window.confirm('Remove this hub override? The hub returns to the global default.')) return;
        try {
            await CustomAxios.delete(`staff/payroll-rules/${ownOverride.id}/`);
            showToast('success', 'Removed', 'Hub override removed; the global default now applies');
            fetchRules();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Could not remove the override'));
        }
    };

    const editGlobal = () => {
        if (!globalRow) return;
        setForm({ ...EMPTY_RULES, ...globalRow });
        setEditingId(globalRow.id);
        showToast('info', 'Editing the global default', 'Changes here apply to every hub without an override.');
    };

    const saveSalary = async () => {
        if (!salaryForm.staff_id || salaryForm.monthly_gross === '') {
            showToast('error', 'Validation Error', 'Pick a staff member and enter a gross amount');
            return;
        }
        try {
            setSaving(true);
            await CustomAxios.post('staff/salary-config/', salaryForm);
            showToast('success', 'Saved', 'New salary version created — earlier months keep the old rate');
            setSalaryModal(false);
            setSalaryForm({ staff_id: '', monthly_gross: '', effective_from: dayjs().format('YYYY-MM-DD') });
            fetchSalaries();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Could not save the salary version'));
        } finally {
            setSaving(false);
        }
    };

    const deleteSalary = async (item) => {
        if (!window.confirm(
            `Remove the ₹${Number(item.monthly_gross).toLocaleString('en-IN')} version effective ` +
            `${dayjs(item.effective_from).format('DD MMM YYYY')}? This is for correcting a typo, not rewriting history.`
        )) return;
        try {
            await CustomAxios.delete(`staff/salary-config/${item.id}/`);
            showToast('success', 'Removed', 'Salary version removed');
            fetchSalaries();
        } catch (error) {
            showToast('error', 'Blocked', errorMessage(error, 'Could not remove the version'));
        }
    };

    const unlockMonth = async (lock) => {
        if (!window.confirm(`Reopen ${lock.month_label}? Already-disbursed figures will be able to move again.`)) return;
        try {
            await CustomAxios.delete(`staff/payroll/locks/${lock.id}/`);
            showToast('success', 'Reopened', `${lock.month_label} can be edited again`);
            fetchLocks();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Could not reopen the month'));
        }
    };

    const hubName = useMemo(
        () => inventory?.find(inv => inv.value === hubId)?.name || 'this hub',
        [inventory, hubId]
    );

    const sampleGross = 16000;
    const previewRate = useMemo(() => {
        const divisor = Number(form.rate_divisor) || 1;
        if (form.per_day_rate_basis !== 'fixed_divisor') return null;
        return (sampleGross / divisor).toFixed(2);
    }, [form.per_day_rate_basis, form.rate_divisor]);

    return (
        <Box sx={{ p: 3 }}>
            <Toast ref={toast} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Typography variant="h5">Payroll Configuration</Typography>
                <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip bg="#FFF1F0" border="#F5222D" color="#F5222D" label="HR Admin only" />
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                        Applies to <b>{hubName}</b>
                    </Typography>
                    {ownOverride && (
                        <Button variant="outlined" onClick={removeOverride}>Remove override</Button>
                    )}
                    <Button variant="contained" onClick={saveRules} disabled={saving || loading}>
                        {editingId ? 'Save rules' : 'Create hub override'}
                    </Button>
                </Box>
            </Box>

            {!loading && (
                <Alert
                    severity={ownOverride ? 'success' : 'info'}
                    sx={{ mb: 3, fontSize: '12.5px' }}
                    action={!ownOverride && globalRow ? (
                        <Button size="small" onClick={editGlobal}>Edit global instead</Button>
                    ) : null}
                >
                    {ownOverride ? (
                        <>
                            <b>{hubName} has its own rules.</b> Removing the override returns it to the
                            global default. The global row itself can only be edited, never deleted.
                        </>
                    ) : (
                        <>
                            <b>{hubName} has no override</b> and is running on{' '}
                            {globalRow ? 'the global default' : 'the built-in defaults (26-day divisor)'}.
                            Saving creates an override for this hub only.
                        </>
                    )}
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <Box sx={{ flex: '1 1 460px', minWidth: '320px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <Box sx={{ backgroundColor: 'background.light', borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012', overflow: 'hidden' }}>
                        <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>Per-day rate basis</Typography>
                            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>what gross is divided by</Typography>
                        </Box>
                        <Box sx={{ p: '16px 18px' }}>
                            <ToggleButtonGroup
                                exclusive size="small" value={form.per_day_rate_basis}
                                onChange={(e, value) => value && setForm({ ...form, per_day_rate_basis: value })}
                                sx={{ mb: 2, flexWrap: 'wrap' }}
                            >
                                {RATE_BASES.map(b => (
                                    <ToggleButton key={b.value} value={b.value} sx={{ px: 2, fontSize: '11px' }}>
                                        {b.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>

                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    fullWidth label="Divisor" type="number"
                                    disabled={form.per_day_rate_basis !== 'fixed_divisor'}
                                    value={form.rate_divisor}
                                    onChange={(e) => setForm({ ...form, rate_divisor: e.target.value })}
                                    helperText="An integer, so 30 is one edit away"
                                />
                                <TextField
                                    fullWidth label="Currently resolves to" disabled
                                    value={previewRate
                                        ? `₹16,000 ÷ ${form.rate_divisor} = ₹${previewRate} / day`
                                        : 'Varies by month'}
                                    helperText="Worked on a sample ₹16,000 gross"
                                />
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ backgroundColor: 'background.light', borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012', overflow: 'hidden' }}>
                        <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>Deduction rules</Typography>
                        </Box>
                        <Box sx={{ p: '16px 18px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    fullWidth label="Lates per deduction" type="number"
                                    value={form.lates_per_deduction}
                                    onChange={(e) => setForm({ ...form, lates_per_deduction: e.target.value })}
                                    helperText="0 disables the rule"
                                />
                                <TextField
                                    fullWidth label="Days deducted each time" type="number"
                                    inputProps={{ step: '0.5' }}
                                    value={form.late_deduction_days}
                                    onChange={(e) => setForm({ ...form, late_deduction_days: e.target.value })}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    fullWidth label="Permission free hours / month" type="number"
                                    inputProps={{ step: '0.5' }}
                                    value={form.permission_free_hours}
                                    onChange={(e) => setForm({ ...form, permission_free_hours: e.target.value })}
                                />
                                <TextField
                                    fullWidth label="Comp-off validity (days)" type="number"
                                    value={form.comp_off_validity_days}
                                    onChange={(e) => setForm({ ...form, comp_off_validity_days: e.target.value })}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Missing punch</InputLabel>
                                    <Select
                                        value={form.missing_punch_handling} label="Missing punch"
                                        onChange={(e) => setForm({ ...form, missing_punch_handling: e.target.value })}
                                    >
                                        {MISSING_PUNCH_OPTIONS.map(o => (
                                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    fullWidth label="Regularization window (days)" type="number"
                                    value={form.regularization_window_days}
                                    onChange={(e) => setForm({ ...form, regularization_window_days: e.target.value })}
                                />
                            </Box>
                            <FormControl fullWidth>
                                <InputLabel>Balance overflow</InputLabel>
                                <Select
                                    value={form.leave_balance_overflow} label="Balance overflow"
                                    onChange={(e) => setForm({ ...form, leave_balance_overflow: e.target.value })}
                                >
                                    {OVERFLOW_OPTIONS.map(o => (
                                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box sx={{
                                border: '1px solid', borderColor: 'secondary.border',
                                borderRadius: '6px', padding: '11px 14px', backgroundColor: 'background.upload'
                            }}>
                                <Typography sx={{ fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.secondary' }}>
                                    Enforced regardless of settings
                                </Typography>
                                <Typography sx={{ fontSize: '12px', mt: 0.5 }}>
                                    Deduction can never exceed the pro-rated base pay, and net salary
                                    never goes below zero. Correctness, not preference — there is no
                                    toggle for these.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ flex: '1 1 380px', minWidth: '320px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012' }}>
                        <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Box>
                                <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>Salary by staff</Typography>
                                <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                                    versioned · never overwritten
                                </Typography>
                            </Box>
                            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setSalaryModal(true)}>
                                Set salary
                            </Button>
                        </Box>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {['Staff', 'Gross ₹', 'Effective from', ''].map((h, i) => (
                                        <TableCell key={i} sx={{ fontWeight: 600, backgroundColor: 'background.table', fontSize: '11px' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow><TableCell colSpan={4}>
                                        {[0, 1].map(i => <Skeleton key={i} animation="wave" sx={{ height: '40px' }} />)}
                                    </TableCell></TableRow>
                                ) : salaries.length ? salaries.map((item, ind) => (
                                    <TableRow key={item.id} sx={{ opacity: item.is_current ? 1 : 0.55 }}>
                                        <TableCell sx={{ fontSize: '12px' }}>
                                            <b>{item.staff_name}</b><br />
                                            <Typography component="span" sx={{ fontSize: '10px', fontFamily: 'monospace', color: 'text.secondary' }}>
                                                {item.is_current ? item.employee_id : 'superseded'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                            {Number(item.monthly_gross).toLocaleString('en-IN')}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                            {dayjs(item.effective_from).format('DD MMM YYYY')}
                                            {item.is_current && (
                                                <Box component="span" sx={{ ml: 1 }}>
                                                    <Chip bg="#F6FFED" border="#52C41A" color="#52C41A" label="current" />
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <PrimeMenu
                                                model={[{ label: 'Remove version', command: () => deleteSalary(item) }]}
                                                popup ref={salaryMenuRefs.current[ind]} className="custom-menu"
                                                style={{ marginTop: '-10px' }}
                                            />
                                            <Tooltip title="Actions" arrow>
                                                <IconButton size="small" onClick={(e) => salaryMenuRefs.current[ind]?.current?.toggle(e)}>
                                                    <MoreHorizSharpIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 3, fontSize: '12px' }}>
                                        No salaries set. Until one exists, the monthly report shows
                                        attendance only for that staff member.
                                    </TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012' }}>
                        <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>Disbursement locks</Typography>
                            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                                locks the inputs, not the outputs
                            </Typography>
                        </Box>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {['Month', 'Locked by', 'On', ''].map((h, i) => (
                                        <TableCell key={i} sx={{ fontWeight: 600, backgroundColor: 'background.table', fontSize: '11px' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {locks.length ? locks.map((lock, ind) => (
                                    <TableRow key={lock.id}>
                                        <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                            <LockOutlinedIcon sx={{ fontSize: 13, color: '#A85700', mr: 0.5, verticalAlign: '-2px' }} />
                                            {lock.month_label}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '12px' }}>{lock.locked_by_name || '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                            {dayjs(lock.locked_on).format('DD MMM YYYY')}
                                        </TableCell>
                                        <TableCell>
                                            {isSuperAdmin ? (
                                                <>
                                                    <PrimeMenu
                                                        model={[{ label: 'Reopen month', command: () => unlockMonth(lock) }]}
                                                        popup ref={lockMenuRefs.current[ind]} className="custom-menu"
                                                        style={{ marginTop: '-10px' }}
                                                    />
                                                    <Tooltip title="Actions" arrow>
                                                        <IconButton size="small" onClick={(e) => lockMenuRefs.current[ind]?.current?.toggle(e)}>
                                                            <MoreHorizSharpIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            ) : null}
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 3, fontSize: '12px' }}>
                                        No months locked yet
                                    </TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <Box sx={{ p: '12px 18px', borderTop: '1px solid #EDEDED' }}>
                            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                                Lock a month from the Monthly Report once salary has been paid.
                                {isSuperAdmin
                                    ? ' Reopening is available to you as super admin.'
                                    : ' Reopening a locked month is super admin only.'}
                            </Typography>
                        </Box>
                    </TableContainer>
                </Box>
            </Box>

            <Modal open={salaryModal} onClose={() => setSalaryModal(false)}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', width: { xs: '92%', sm: 440 },
                    bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2
                }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Set Salary</Typography>

                    <FormControl fullWidth required sx={{ mb: 2 }}>
                        <InputLabel>Staff</InputLabel>
                        <Select
                            value={salaryForm.staff_id} label="Staff"
                            onChange={(e) => setSalaryForm({ ...salaryForm, staff_id: e.target.value })}
                        >
                            {staffOptions.map(s => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth required label="Monthly gross" type="number"
                        value={salaryForm.monthly_gross}
                        onChange={(e) => setSalaryForm({ ...salaryForm, monthly_gross: e.target.value })}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth required label="Effective from" type="date"
                        value={salaryForm.effective_from}
                        onChange={(e) => setSalaryForm({ ...salaryForm, effective_from: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        helperText="Creates a new version. Earlier months keep the rate that applied then."
                        sx={{ mb: 2 }}
                    />

                    <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', mb: 2 }}>
                        A month uses the version in force on its <b>last day</b>, so a raise dated
                        mid-month applies to the whole of that month.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button onClick={() => setSalaryModal(false)}>Cancel</Button>
                        <Button variant="contained" onClick={saveSalary} disabled={saving}>Save version</Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}
